import moment from 'moment';
import { cloneDeep, filter, findIndex, isEqual, lowerCase, orderBy, pull, remove, uniqBy } from 'lodash';
import { action, observable, toJS } from 'mobx';

import { ActionType } from '../types/ActionType';
import { IOrderType, OrderDirection } from '../types/OrderType';
import { IConditionalsType, IFilterType } from '../types/ProfileTypes';
import { PaginationUtils } from '../utils/PaginationUtils';
import { filterHasNoValueFn } from '../utils/utils';
import { ParentStore } from './ParentStore';

export class ParentPaginateStore extends ParentStore {
  @observable
  page: number;

  @observable
  size: number;

  @observable
  count: number;

  @observable
  action: ActionType;

  @observable
  public orders: IOrderType[];

  @observable
  public filters: IFilterType[];

  @observable
  public conditionals: IConditionalsType[];

  @observable
  fetchedDataFromServer: boolean;

  @observable
  search: string;

  @observable.shallow
  public staticFilters: IFilterType[];

  serviceContainer: any;

  constructor(serviceContainer: any) {
    super(serviceContainer);
    this.search = '';
    this.count = 0;
    this.page = 1;
    this.size = 50;
    this.isLoading = false;
    this.action = 'LIST';
    this.filters = [];
    this.staticFilters = [];
    this.conditionals = [];
    this.orders = [];
    this.serviceContainer = serviceContainer;
    this.fetchedDataFromServer = false;
  }

  /*@computed
  pageCount() {
    return Math.ceil(this.count / this.size);
  }*/

  @action setSearch = (criteria: string) => {
    this.search = criteria;
  };

  @action setFetchedDataFromServer = (value: boolean) => {
    this.fetchedDataFromServer = value;
  };

  @action
  setLoading(value: boolean) {
    this.isLoading = value;
  }

  @action
  setElement(element: any) {
    this.element = element;
  }

  @action
  resetElement() {
    this.element = {};
  }

  @action
  setElements(elements: any[]) {
    this.setFetchedDataFromServer(true);
    this.elements = elements;
  }

  @action
  setSelectedElements(item: any[]) {
    this.selectedElements = uniqBy(item, 'id');
  }

  @action
  resetSelectedElements() {
    this.selectedElements = [];
  }

  @action
  pushSelectedElements(item: any) {
    if (findIndex(this.selectedElements, { id: item.id }) === -1) {
      this.selectedElements.push(item);
    }
  }

  @action
  pullSelectedElements(item: any) {
    this.selectedElements = pull(this.selectedElements, item);
  }

  @action
  setPage(item: number) {
    this.page = item;
  }

  @action
  setSize(value: number) {
    this.size = value;
  }

  @action
  setCount(value: number) {
    this.count = value;
  }

  @action
  setPageCount(value: number) {
    this.count = value;
  }
  @action
  setOrders(value: any[]) {
    this.orders = value;
  }

  @action
  setFilters(value: IFilterType[]) {
    this.filters = value;
  }

  @action
  setConditionals(value: IConditionalsType[]) {
    this.conditionals = value;
  }

  @action setStaticFilters = (filters: IFilterType[]) => {
    this.staticFilters = filters;
  };

  @action
  createStore() {
    this.element = {};
    this.elements = [];
    this.selectedElements = [];
    this.count = 0;
    this.page = 1;
    this.size = 10;
    this.isLoading = true;
    this.action = 'LIST';
    this.filters = [];
    this.conditionals = [];
    this.orders = [];
  }

  setFilterElements(data: any) {
    this.setElements(data);
    this.filteredData();
    this.orderData();
    this.paginateData();
  }

  filterHasNoValue(filter: IFilterType, allowNull?: boolean) {
    return filterHasNoValueFn(filter, allowNull);
  }

  @action
  public addFilter(value: IFilterType, type?: 'DYNAMIC' | 'STATIC', allowNull?: boolean) {
    const filters = type === 'STATIC' ? toJS(this.staticFilters) : toJS(this.filters);
    const updatedFilters = this.updateListOfFilters(filters, [value], allowNull);
    type === 'STATIC' ? this.setStaticFilters(updatedFilters) : this.setFilters(updatedFilters);
  }

  public addMultipleFilters(values: IFilterType[]) {
    const updatedFilters = this.updateListOfFilters(toJS(this.filters), values);
    this.setFilters(updatedFilters);
  }

  updateListOfFilters = (filters: IFilterType[], values: IFilterType[], allowNull?: boolean) => {
    let returnFilters = cloneDeep(filters);
    values.forEach((val: IFilterType) => {
      const index = findIndex(returnFilters, (filter: IFilterType) => {
        return filter.field === val.field;
      });

      if (index !== -1) {
        if (this.filterHasNoValue(val, allowNull)) {
          returnFilters = returnFilters.filter(fil => fil?.field !== val.field);
        } else {
          returnFilters[index] = val;
        }
      } else if (!this.filterHasNoValue(val, allowNull)) {
        returnFilters.push(val);
      }
    });
    return returnFilters;
  };

  public getTransformFiltersToBackend(type?: 'DYNAMIC' | 'STATIC', includeBooleans?: boolean): IFilterType[] {
    const filters = type === 'STATIC' ? this.staticFilters : this.filters;
    return cloneDeep(filters)
      .filter((value: IFilterType) => value.active)
      .filter((value: IFilterType) => value.value !== '' && !isEqual(value.value, []) && value.value !== null)
      .map((value: IFilterType) => {
        let v = value;
        if (v.type === 'BOOLEAN' && !includeBooleans) {
          v = this.transformBooleanField(v);
        }
        if (v.type === 'TIME' && v.field === 'averageTimeTaken') {
          v = this.transformAverageTimeTaken(v);
        }
        return {
          field: v.field,
          value: v.value,
          operator: v.operator
        };
      });
  }

  public transformBooleanField(value: IFilterType): IFilterType {
    const v = toJS(value);
    switch (v.value) {
      case true:
        v.value = 1;
        break;
      case false:
        v.value = 0;
        break;
      case null:
        v.value = null;
        break;
    }
    return v;
  }

  public transformAverageTimeTaken(value: IFilterType): IFilterType {
    const date = moment(value.value);
    value.value = date.minutes() * 60 + date.second();
    return value;
  }

  public transformDateRange(value: IFilterType): IFilterType {
    value.value = value?.value?.map((item: any) => item && moment(item).format('YYYY-MM-DD[T]HH:mm:00.000[Z]'));
    return value;
  }

  @action
  public removeFilter(value: IFilterType, type?: 'DYNAMIC' | 'STATIC') {
    const filters = type === 'STATIC' ? this.staticFilters : this.filters;
    remove(filters, (filter: IFilterType) => {
      return filter.field === value.field;
    });
  }

  @action
  public clearFilters() {
    this.filters = [];
  }

  @action
  public addOrder(value: IOrderType) {
    const index = findIndex(this.orders, (order: IOrderType) => {
      return order.property === value.property;
    });
    if (index !== -1) {
      this.orders[index] = value;
    } else {
      this.orders.push(value);
    }
  }

  @action
  public removeOrder(value: IOrderType) {
    remove(this.orders, (order: IOrderType) => {
      return order.property === order.property;
    });
  }

  filteredData() {
    const f: any = {};
    this.filters.forEach((value: IFilterType) => {
      if (value.operator === 'EQUALITY') {
        f[value.field] = value.value;
      }
    });
    this.setElements(filter(this.elements, f));
  }

  orderData() {
    const f: string[] = [];
    const o: OrderDirection[] = [];
    this.orders.forEach((value: IOrderType) => {
      f.push(value.property);
      o.push(value.sortOperation);
    });
    this.setElements(orderBy(this.elements, f, o));
  }

  paginateData() {
    this.setElements(this.paginate(this.elements, this.size, this.page));
  }

  paginate(array: any[], page_size: number, page_number: number) {
    const p = page_number - 1; // because pages logically start with 1, but technically with 0
    return array.slice(p * page_size, (p + 1) * page_size);
  }

  /*
   * Static sorting, filtering and paging
   * */
  applyStaticFilters(data: any[]) {
    let result: any[] = data;
    const ft: IFilterType[] = this.getTransformFiltersToBackend();
    ft.forEach((ftr: IFilterType) => {
      result = result.filter((val: any) => {
        if (val.hasOwnProperty(ftr.field)) {
          return PaginationUtils.applyFilters(ftr, val[ftr.field]);
        }
        return ft.length !== 1;
      });
    });
    return result;
  }

  applySearchFilter(data: any[]) {
    if (!this.search) {
      return data;
    }
    return data.filter((value: any) => {
      return PaginationUtils.iterateBySearch(value, this.search);
    });
  }

  paginateStaticData(data: any[]) {
    return this.paginate(data, this.size, this.page);
  }

  orderStaticData(data: any[]) {
    const f: string[] = [];
    const o: OrderDirection[] = [];
    this.orders.forEach((value: IOrderType) => {
      f.push(value.property);
      o.push(lowerCase(value.sortOperation) as OrderDirection);
    });
    return orderBy(data, f, o);
  }

  processStaticPaginateData(data: any[], skipPagination?: boolean) {
    let result = data;
    result = this.applySearchFilter(result);
    result = this.applyStaticFilters(result);
    result = this.orderStaticData(result);
    if (skipPagination) {
      return { pData: result, dataLength: result.length };
    }
    return { pData: this.paginateStaticData(result), dataLength: result.length };
  }

  /*
   * End Static sorting, filtering and paging
   * */

  getElements() {
    this.setLoading(true);
    this.serviceContainer
      .getAll()
      .then((response: any) => {
        this.setFilterElements(response.data);
      })
      .catch((err: any) => {
        this.setLoading(false);
        throw err.response;
      });
  }
}
